import { useEffect, useState } from 'react';

import { IconLoader2, IconRefresh } from '@tabler/icons-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { api } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import MainCard from '@/components/MainCard';
import { StatCard } from '@/components/cards';

interface ChurnCandidate {
  company_name: string;
  churn_candidate_count: string;
}

export function ChurnPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ChurnCandidate[]>([]);

  const fetchChurnSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/churn/candidates/summary');
      // Handle nested data structure: response.data.data.data
      const data = response.data?.data?.data || response.data?.data || [];
      setCandidates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to load churn candidates summary', err);
      setError(err.response?.data?.message || 'Failed to load churn candidates summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChurnSummary();
  }, []);

  const handleRefresh = () => {
    fetchChurnSummary();
  };

  return (
    <div>
      <PageHeader
        title="Churn Management"
        subtitle="Monitor and manage customer churn candidates by distributor"
        action={
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <IconRefresh className="size-4" />
            Refresh
          </Button>
        }
      />

      {candidates.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
          <div>
            <StatCard
              title="Total Churn Candidates"
              value={candidates.reduce((sum, c) => sum + parseInt(c.churn_candidate_count || '0', 10), 0).toLocaleString()}
            />
          </div>
          <div>
            <StatCard
              title="Distributors Affected"
              value={candidates.length.toLocaleString()}
            />
          </div>
        </div>
      )}

      <MainCard>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <IconLoader2 className="animate-spin size-6" aria-hidden />
          </div>
        )}

        {!loading && error && (
          <div className="p-6">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {!loading && !error && candidates.length > 0 && (
          <>
            <div className="flex flex-col gap-2 mb-4">
              <Alert variant="info">
                <AlertDescription className="text-inherit">
                  Showing churn candidates grouped by distributor. Total candidates:{' '}
                  {candidates.reduce((sum, c) => sum + parseInt(c.churn_candidate_count || '0', 10), 0).toLocaleString()}
                </AlertDescription>
              </Alert>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distributor</TableHead>
                  <TableHead className="text-right">Churn Candidates</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.company_name || 'Unknown'}</TableCell>
                    <TableCell className="text-right">
                      {parseInt(item.churn_candidate_count || '0', 10).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        {!loading && !error && candidates.length === 0 && (
          <div className="p-6 text-center">
            <Alert variant="info">
              <AlertDescription className="text-inherit">No churn candidates found</AlertDescription>
            </Alert>
          </div>
        )}
      </MainCard>
    </div>
  );
}
